import {
  BookOpen,
  Globe,
  Headphones,
  Heart,
  Leaf,
  Music2,
  Microscope,
  Waves,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { goBack } from "../utils/goBack";
import TopbarInner from "../components/topbar/TopbarInner";
import FeaturedYouTubeRail from "../components/FeaturedYouTubeRail.jsx";
import { Helmet } from "react-helmet-async";

const PLATFORMS = Object.freeze([
  { key: "youtube", label: "YouTube", href: "https://www.youtube.com/@harmonauria", path: "M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" },
  { key: "spotify", label: "Spotify", href: "https://open.spotify.com/intl-fr/artist/1B9hWpZRZuYhrVxPaWBEI3", path: "M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" },
  { key: "apple", label: "Apple Music", href: "https://music.apple.com/us/artist/harmonauria/1812630956", path: "M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" },
  { key: "deezer", label: "Deezer", href: "https://www.deezer.com/fr/artist/321659221", path: "M18.81 11.293h3.914v2.027H18.81zm0 3.199h3.914v2.028H18.81zm0-6.4h3.914v2.026H18.81zm-4.867 9.596h3.914v2.026h-3.914zm0-3.196h3.914v2.027h-3.914zm0-3.2h3.914V13.32h-3.914zm0-3.2h3.914v2.026h-3.914zM9.08 17.688h3.913v2.026H9.08zm0-3.196h3.913v2.027H9.08zm0-3.2h3.913V13.32H9.08zM4.211 17.688h3.914v2.026H4.211zm0-3.196h3.914v2.027H4.211zM-.658 17.688h3.913v2.026H-.658z" },
]);

export default function Apropos({ showThemeDropdown = true, isPremium = false, longestStreak = 0 }) {
  const navigate = useNavigate();
  return (
    <>
      <Helmet>
        <title>À propos — l&apos;histoire d&apos;Harmonauria</title>
        <meta name="description" content="Harmonauria est né d'une passion pour la musique et d'une envie sincère d'être utile. L'histoire d'un refuge où les musiques sont composées à la main." />
        <link rel="canonical" href="https://harmonauria.fr/apropos" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="À propos — l&apos;histoire d&apos;Harmonauria" />
        <meta property="og:description" content="Un refuge né d'une passion pour la musique et d'une envie sincère d'être utile." />
        <meta property="og:url" content="https://harmonauria.fr/apropos" />
      </Helmet>

<TopbarInner
  title="À propos"
  onBack={() => goBack(navigate)}
  titleColor="#36495B"
  showThemeDropdown={showThemeDropdown}
  isPremium={isPremium}
  longestStreak={longestStreak}
/>

      <main className="apropos-page">
        <header className="page-hero">
          <p className="page-hero__eyebrow">
            L’histoire de Harmonauria
          </p>

          <h1 className="page-hero__title">À propos</h1>

          <p className="page-hero__lead">
            L’histoire d’un refuge né d’une passion pour la musique,
            ce qui l’inspire, et où l’écouter.
          </p>
        </header>

        <article className="about-content">
          <section className="about-section" aria-labelledby="about-passion">
            <div className="about-heading">
              <span className="about-icon" aria-hidden>
                <Music2 size={19} />
              </span>
              <h2 id="about-passion" className="subtitle">
                Une aventure née d’une passion
              </h2>
            </div>

            <p className="text">
              Tout a commencé avec une passion profonde pour la musique
              et une question simple :{" "}
              <em>et si, au-delà de plaire, elle pouvait réellement aider ?</em>
            </p>

            <p className="text">
              Harmonauria est né de la rencontre entre ma{" "}
              <strong>passion pour la composition musicale</strong> et
              l’envie d’être{" "}
              <strong>utile dans les moments ordinaires</strong> : avant
              de dormir, entre deux rendez-vous, lorsque le stress monte
              ou lorsque vous ressentez simplement le besoin de souffler.
            </p>

            <p className="text">
              Ce projet est sincère, personnel et en constante évolution.
              Chaque morceau que vous écoutez ici a été pensé, ressenti et
              composé avec cette intention.
            </p>
          </section>

          <section className="about-section" aria-labelledby="about-vision">
            <div className="about-heading">
              <span className="about-icon" aria-hidden>
                <Leaf size={19} />
              </span>
              <h2 id="about-vision" className="subtitle">
                La vision
              </h2>
            </div>

            <p className="text">
              Harmonauria a été imaginé comme un{" "}
              <strong>refuge numérique</strong> : un endroit où vous
              pouvez mettre le bruit extérieur à distance et retrouver
              votre propre rythme.
            </p>

            <ul className="list">
              <li>Vous apaiser avant de dormir.</li>
              <li>Vous recentrer entre deux rendez-vous.</li>
              <li>Retrouver de l’énergie en douceur.</li>
              <li>Prendre simplement un moment pour vous.</li>
            </ul>
          </section>

          <section className="about-section" aria-labelledby="about-content">
            <div className="about-heading">
              <span className="about-icon" aria-hidden>
                <Headphones size={19} />
              </span>
              <h2 id="about-content" className="subtitle">
                Ce que vous trouverez ici
              </h2>
            </div>

            <p className="text">
              Harmonauria réunit des{" "}
              <strong>paysages sonores</strong>, des{" "}
              <strong>respirations guidées</strong> et des{" "}
              <strong>guides pratiques</strong> conçus pour vous
              accompagner vers davantage de calme et de détente.
            </p>

            <ul className="list">
              <li><strong>Accessible</strong>, même lorsque vous débutez.</li>
              <li><strong>Bienveillant</strong>, sans objectif de performance.</li>
              <li><strong>Intuitif</strong>, pour choisir ce qui vous convient sur le moment.</li>
            </ul>
          </section>

          <section
            className="about-section about-section--highlight"
            aria-labelledby="about-human"
          >
            <div className="about-heading">
              <span className="about-icon about-icon--highlight" aria-hidden>
                <Heart size={19} />
              </span>
              <h2 id="about-human" className="subtitle">
                Une musique composée par un humain
              </h2>
            </div>

            <p className="text">
              À une époque où l’intelligence artificielle peut générer
              des milliers de titres en quelques secondes, j’ai fait un
              choix différent :{" "}
              <strong>
                chaque composition est créée à la main, sans génération
                musicale par intelligence artificielle.
              </strong>
            </p>

            <p className="text">
              Je crois que la musique dédiée au calme mérite d’être
              habitée. Chaque progression d’accords, chaque texture et
              chaque silence sont choisis avec l’intention d’accompagner
              un moment précis.
            </p>

            <p className="text">
              Ce n’est pas une musique produite en masse. C’est une{" "}
              <strong>création artisanale</strong>, portée par une
              sensibilité humaine et une intention sincère.
            </p>
           <Link to="/frequences" state={{ from: "/apropos" }} className="about-explore-card">
              <span className="about-explore-card__icon" aria-hidden="true">
                <Waves size={19} />
              </span>
              <span className="about-explore-card__content">
                <strong>Sons &amp; fréquences</strong>
                <span>Découvrez les instruments, les textures et les fréquences choisis pour accompagner chaque moment.</span>
              </span>
              <span className="about-explore-card__arrow" aria-hidden="true">→</span>
            </Link>
          </section>

          <section className="about-section" aria-labelledby="about-science">
            <div className="about-heading">
              <span className="about-icon" aria-hidden>
                <Microscope size={19} />
              </span>
              <h2 id="about-science" className="subtitle">
                Une approche éclairée par la recherche
              </h2>
            </div>

            <p className="text">
              Harmonauria s’appuie notamment sur des pratiques de
              respiration et d’écoute étudiées dans la recherche, comme
              la cohérence cardiaque, le soupir physiologique, certains
              usages des battements binauraux et l’écoute de sons
              naturels.
            </p>

            <p className="text">
              D’autres approches sonores sont proposées comme des
              expériences personnelles à explorer, sans promesse
              médicale et sans jamais remplacer l’accompagnement d’un
              professionnel de santé.
            </p>
            <Link to="/science" state={{ from: "/apropos" }} className="about-explore-card">
              <span className="about-explore-card__icon" aria-hidden="true">
                <BookOpen size={19} />
              </span>
              <span className="about-explore-card__content">
                <strong>Sources scientifiques</strong>
                <span>Consultez les études sur lesquelles s’appuient les respirations et les paysages sonores proposés.</span>
              </span>
              <span className="about-explore-card__arrow" aria-hidden="true">→</span>
            </Link>
          </section>

          <section className="about-section" aria-labelledby="about-philosophy">
            <div className="about-heading">
              <span className="about-icon" aria-hidden>
                <Leaf size={19} />
              </span>
              <h2 id="about-philosophy" className="subtitle">
                Une philosophie simple
              </h2>
            </div>

            <p className="text">
              Quelques minutes peuvent parfois suffire pour créer une
              pause dans une journée trop pleine : une respiration
              guidée, un paysage sonore ou un rituel du soir.
            </p>

            <p className="text">
              L’idée n’est pas d’en faire davantage, mais de retrouver
              régulièrement un moment plus calme, à votre rythme et sans
              pression.
            </p>
          </section>

          <section
            className="about-section about-section--last"
            aria-labelledby="about-listen"
          >
            <div className="about-heading">
              <span className="about-icon" aria-hidden>
                <Globe size={19} />
              </span>
              <h2 id="about-listen" className="subtitle">
                Écouter Harmonauria ailleurs
              </h2>
            </div>

            <div className="about-stack">
              <FeaturedYouTubeRail />
              <section className="discover-platforms" aria-label="Plateformes d’écoute">
                <p className="discover-platforms__subtitle">
                  Choisissez simplement la plateforme qui vous convient.
                </p>
                <div className="discover-platforms__grid">
{PLATFORMS.map((platform) => (
  <a
    key={platform.key}
    href={platform.href}
    target="_blank"
    rel="noopener noreferrer"
    className={`discover-platform discover-platform--${platform.key}`}
  >
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
      <path d={platform.path} />
    </svg>
    {platform.label}
  </a>
))}
                </div>

                <p className="discover-platforms__hint">
                  Vos moments de calme restent accessibles, même loin du refuge.
                </p>
              </section>
            </div>
          </section>

          <div className="about-signature">
            <p className="about-thanks">
              Merci d’être ici. Accordez-vous ce moment, à votre rythme.
            </p>
          </div>
        </article>

        <style>{`
          .apropos-page,
          .apropos-page * {
            box-sizing: border-box;
          }

          .apropos-page {
            width: 100%;
            min-height: 100%;
            margin-top: 88px;
            padding: 0 20px 100px;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 42px;
          }

          .about-content {
            width: min(100%, 920px);
            margin: 0 auto;
          }

          .about-section {
            width: 100%;
            margin: 0 0 40px;
            padding: 0 4px 40px;
            border-bottom: 1px solid rgba(97, 131, 122, 0.16);
          }

          .about-section--last {
            margin-bottom: 0;
            padding-bottom: 0;
            border-bottom: 0;
          }

          .about-section--highlight {
            padding: 30px 32px;
            background: rgba(244, 248, 246, 0.92);
            border: 1px solid rgba(97, 131, 122, 0.2);
            border-radius: 22px;
            box-shadow: 0 16px 38px rgba(54, 73, 91, 0.07);
            backdrop-filter: blur(12px);
            -webkit-backdrop-filter: blur(12px);
          }

          .about-heading {
            margin-bottom: 18px;
            display: flex;
            align-items: center;
            gap: 13px;
          }

          .about-icon {
            width: 38px;
            height: 38px;
            flex: 0 0 38px;
            display: inline-grid;
            place-items: center;
            color: #5d7d74;
            background: #edf5f2;
            border: 1px solid #dcebe6;
            border-radius: 50%;
          }

          .about-icon--highlight {
            color: #ffffff;
            background: #6f9087;
            border-color: #6f9087;
          }

          .subtitle {
            margin: 0;
            color: #36495b;
            font-family: "Sora", system-ui, sans-serif;
            font-size: 1.28rem;
            font-weight: 700;
            line-height: 1.3;
          }

          .text {
            margin: 0 0 17px;
            color: #5f6e75;
            font-size: 1rem;
            line-height: 1.8;
          }

          .text:last-child {
            margin-bottom: 0;
          }

          .text strong,
          .list strong {
            color: #445d56;
            font-weight: 700;
          }

          .text em {
            color: #56766e;
          }

          .list {
            margin: 20px 0 0;
            padding: 0;
            display: grid;
            gap: 12px;
            color: #5f6e75;
            font-size: 1rem;
            line-height: 1.65;
            list-style: none;
          }

          .list li {
            position: relative;
            padding-left: 22px;
          }

          .list li::before {
            content: "";
            position: absolute;
            top: 0.72em;
            left: 2px;
            width: 6px;
            height: 6px;
            background: #86a49b;
            border-radius: 50%;
            transform: translateY(-50%);
          }

          /* Signature finale */
          .about-signature {
            margin-top: 40px;
            padding: 25px 20px;
            text-align: center;
            background: rgba(247, 250, 249, 0.86);
            border: 1px solid rgba(97, 131, 122, 0.15);
            border-radius: 18px;
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
          }

          .about-thanks {
            margin: 0;
            color: #78868b;
            font-size: 0.9rem;
            line-height: 1.6;
          }

          /* Cards pleine largeur */
          .about-stack {
            display: grid;
            gap: 14px;
          }

          .about-explore-card {
            width: 100%;
            min-width: 0;
            padding: 20px 24px;
            display: grid;
            grid-template-columns: 42px minmax(0, 1fr) auto;
            align-items: center;
            gap: 14px;
            background: rgba(255, 255, 255, 0.82);
            border: 1px solid rgba(97, 131, 122, 0.15);
            border-radius: 22px;
            box-shadow: 0 12px 30px rgba(54, 73, 91, 0.06);
            text-decoration: none;
            transition: transform 180ms ease, border-color 180ms ease, background 180ms ease, box-shadow 180ms ease;
          }

          .about-explore-card__icon {
            width: 42px;
            height: 42px;
            display: grid;
            place-items: center;
            color: #5d7d74;
            background: #edf5f2;
            border: 1px solid #dcebe6;
            border-radius: 50%;
          }

          .about-explore-card__content {
            min-width: 0;
            display: grid;
            gap: 4px;
          }

          .about-explore-card__content strong {
            color: #36495b;
            font-family: "Sora", system-ui, sans-serif;
            font-size: 0.96rem;
            font-weight: 700;
          }

          .about-explore-card__content > span {
            color: #748187;
            font-size: 0.84rem;
            line-height: 1.5;
          }

          .about-explore-card__arrow {
            color: #718d85;
            font-size: 1.15rem;
            font-weight: 700;
          }

          .about-section .about-explore-card {
            margin-top: 24px;
          }

          /* YouTube + plateformes */
          .apropos-page .featured-youtube-card,
          .discover-platforms {
            width: 100%;
            margin: 0;
            padding: clamp(18px, 2.4vw, 24px);
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: clamp(20px, 3vw, 28px);
            border: 1px solid rgba(97, 131, 122, 0.15);
            border-radius: 22px;
            background: radial-gradient(circle at 100% 0%, rgba(203, 226, 219, 0.36), transparent 40%), rgba(255, 255, 255, 0.72);
            box-shadow: 0 16px 38px rgba(49, 72, 66, 0.07), inset 0 1px 0 rgba(255, 255, 255, 0.94);
            backdrop-filter: blur(18px) saturate(125%);
            -webkit-backdrop-filter: blur(18px) saturate(125%);
            text-align: center;
          }

          .apropos-page .featured-youtube-card__inner {
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 19px;
          }

          .apropos-page .featured-youtube-single__media {
            border: 1px solid rgba(97, 131, 122, 0.13);
            border-radius: 17px;
            box-shadow: none;
          }

          .apropos-page .featured-youtube-single__title {
            color: #7a8985;
            font-size: 0.8rem;
            line-height: 1.5;
          }

          .apropos-page .featured-youtube-card__actions {
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 10px;
          }

          .apropos-page .featured-youtube-card__button {
            min-width: 180px;
            min-height: 44px;
            padding: 11px 18px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border: 1px solid #61837a;
            border-radius: 13px;
            background: #61837a;
            color: #ffffff;
            font-size: 0.86rem;
            font-weight: 700;
            text-decoration: none;
            box-shadow: 0 8px 20px rgba(97, 131, 122, 0.15);
            transition: background 200ms ease, border-color 200ms ease, transform 200ms ease;
          }

          .discover-platforms__subtitle {
            margin: 0;
            color: #506866;
            font-size: 0.9rem;
            line-height: 1.6;
            text-align: center;
          }

          .discover-platforms__grid {
            width: 100%;
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 11px;
          }

          .discover-platform {
            width: 100%;
            min-height: 54px;
            padding: 11px 12px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 9px;
            border: 1px solid rgba(97, 131, 122, 0.13);
            border-radius: 16px;
            background: rgba(255, 255, 255, 0.58);
            color: #405b54;
            font-size: 0.84rem;
            font-weight: 750;
            line-height: 1;
            text-decoration: none;
            box-shadow: 0 8px 22px rgba(49, 72, 66, 0.045), inset 0 1px 0 rgba(255, 255, 255, 0.86);
            transition: transform 180ms ease, border-color 180ms ease, background 180ms ease, box-shadow 180ms ease;
          }

          .discover-platform svg { flex: 0 0 auto; }
          .discover-platform--youtube svg { color: #c4302b; }
          .discover-platform--spotify svg { color: #1db954; }
          .discover-platform--apple svg { color: #fc3c44; }
          .discover-platform--deezer svg { color: #ff6600; }

          .discover-platforms__hint {
            max-width: 42ch;
            margin: 0 auto;
            color: rgba(80, 104, 102, 0.68);
            font-size: 0.78rem;
            line-height: 1.5;
            text-align: center;
            text-wrap: balance;
          }

          .about-explore-card:focus-visible,
          .discover-platform:focus-visible,
          .apropos-page .featured-youtube-card__button:focus-visible {
            outline: 3px solid rgba(118, 165, 167, 0.35);
            outline-offset: 3px;
          }

          @media (hover: hover) and (pointer: fine) {
            .about-explore-card:hover {
              transform: translateY(-2px);
              border-color: rgba(97, 131, 122, 0.28);
              background: #ffffff;
              box-shadow: 0 17px 36px rgba(54, 73, 91, 0.1);
            }

            .discover-platform:hover {
              transform: translateY(-2px);
              border-color: rgba(97, 131, 122, 0.26);
              background: rgba(255, 255, 255, 0.88);
              box-shadow: 0 14px 30px rgba(49, 72, 66, 0.09);
            }

            .apropos-page .featured-youtube-card__button:hover {
              transform: translateY(-1px);
              border-color: #56776e;
              background: #56776e;
            }
          }

          @media (max-width: 767px) {
            .apropos-page {
              margin-top: 94px;
              padding: 0 14px 72px;
              gap: 32px;
            }

            .about-section {
              margin-bottom: 32px;
              padding: 0 2px 32px;
            }

            .about-section--last {
              margin-bottom: 0;
              padding-bottom: 0;
            }

            .about-section--highlight {
              padding: 23px 19px;
              border-radius: 20px;
            }

            .about-heading {
              gap: 11px;
            }

            .about-icon {
              width: 34px;
              height: 34px;
              flex-basis: 34px;
            }

            .about-icon svg {
              width: 17px;
              height: 17px;
            }

            .subtitle {
              font-size: 1.12rem;
            }

            .text,
            .list {
              font-size: 0.96rem;
            }

            .about-signature {
              margin-top: 32px;
              padding: 22px 15px;
            }

            .about-explore-card {
              padding: 16px;
              grid-template-columns: 38px minmax(0, 1fr) auto;
              gap: 11px;
              border-radius: 20px;
            }

            .apropos-page .featured-youtube-card,
            .discover-platforms {
              padding: 22px 18px;
              border-radius: 20px;
            }

            .discover-platforms__grid {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }
          }

          @media (max-width: 390px) {
            .discover-platforms__grid {
              grid-template-columns: 1fr;
            }
          }

          @media (prefers-reduced-motion: reduce) {
            .about-explore-card,
            .discover-platform,
            .apropos-page .featured-youtube-card__button {
              transition: none;
            }
          }
        `}</style>
      </main>
    </>
  );
}